import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react-lite'
import { v4 as uuidv4 } from 'uuid'

import { useStores } from '../../../cls/stores/MobXProvider'

import {
	Filter,
	UserString,
	LstTable,
	FormatedTimestamp,
	CustomTable
} from '../../../components/Shared'
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import {
	actLoadDocumentationList,
	actUndoOrDeleteDocumentation
} from '../../../stores/ui-lst-documentations/actions'
import {
	actSaveDocumentation,
	newDocumentation,
	requireDocumentationById
} from '../../../stores/documentation/action'
import { Popconfirm, Select, Space } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import { translate as t } from '../../../cls/TranslationContext'
import { useSmartTranslation } from '../../../cls/TranslationContext'

/**
 * @component LstItem
 * @description Renders a single documentation list row with actions (view, edit, delete/undo).
 * Dynamically adapts options based on user roles and item state (e.g., deleted or not).
 * Triggers navigation and deletion flows.
 * @param {string} id - The unique identifier of the documentation item.
 * @param {boolean} isFirst - Whether this is the first row (for tour targeting).
 */
const LstItem = observer(({ id, isFirst }) => {
	const navigate = useNavigate()
	const { users } = useStores()
	const { loggedInUser, loggedInUserId } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const documentation = requireDocumentationById(id)

	const selfPermission =
		documentation.created_by === loggedInUser.user_id ||
		isAdmin ||
		isSuperAdmin

	const [showConfirmUndoOrDelete, setConfirmUndoOrDelete] = useState(false)
	/**
	 * @function confirmUndoOrDelete
	 * @description Triggers either the undo-delete or soft-delete operation depending on current item status.
	 * Uses the `actUndoOrDeleteDocumentation()` action.
	 */
	const confirmUndoOrDelete = () => {
		setConfirmUndoOrDelete(false)

		if (!!documentation.to_delete) {
			actUndoOrDeleteDocumentation(id, false).catch(console.error)
		} else {
			actUndoOrDeleteDocumentation(id, true).catch(console.error)
		}
	}

	// Define the default actions available for this item
	const _actions = [
		{
			key: 'view',
			label: t('single.view'),
			value: 'view',
			disabled: !selfPermission && !users.canUser('read', 'documentation')
		}
	]

	// Give edit and delete access to only admins or the document's creator
	if (
		isAdmin ||
		isSuperAdmin ||
		documentation.created_by === loggedInUserId
	) {
		_actions.push({
			key: 'data',
			label: t('single.edit'),
			value: 'data',
			disabled: !selfPermission && !users.canUser('read', 'documentation')
		})
		_actions.push({
			key: 'delete',
			label: t('single.delete'),
			value: 'delete',
			disabled:
				!selfPermission && !users.canUser('delete', 'documentation')
		})
	}

	/**
	 * @function handleActions
	 * @description Handles selection from the action dropdown on each list item.
	 * Supports view, edit, and delete/undo operations based on the selected value.
	 * @param {string} value - Selected action key.
	 */
	const handleActions = (value) => {
		switch (value) {
			case 'view':
				navigate(`${id}/view`)
				break
			case 'data':
				navigate(`${id}/edit`)
				break
			case 'undo-delete':
				setConfirmUndoOrDelete(true)
				break
			case 'delete':
				setConfirmUndoOrDelete(true)
				break
			default:
				break
		}
	}

	return (
		<tr>
			<td>
				<Link id={isFirst ? 'target-itm-link' : ''} to={`${id}/view`}>
					{documentation.name}
				</Link>
			</td>
			<td id={isFirst ? 'target-itm-desc' : ''}>
				{documentation.description}
			</td>
			<td id={isFirst ? 'target-itm-info' : ''}>
				<CustomTable
					style={{ maxWidth: '200px' }}
					data={[
						[
							t('single.created-by.label'),
							{
								item: (
									<UserString
										userId={documentation.created_by}
										path="name"
									/>
								)
							}
						],
						[
							t('single.created-at'),
							{
								item: (
									<FormatedTimestamp
										timestamp={documentation.createdTs}
										formatedAs="DD.MM.YYYY hh:mm"
									/>
								)
							}
						]
					]}
				/>
			</td>
			<td>
				<Space
					style={{ width: '100%' }}
					id={isFirst ? 'target-itm-actions' : ''}
					direction="vertical"
				>
					<Select
						value="Actions"
						options={_actions}
						onChange={handleActions}
						style={{ width: 120 }}
					/>
					{documentation.to_delete && (
						<>
							<span>
								{t('single.delete-on')}
								<FormatedTimestamp
									timestamp={documentation.to_delete}
								/>
							</span>
						</>
					)}
				</Space>
				<Popconfirm
					title={
						documentation.to_delete
							? t('single.confirm-undo-delete')
							: t('single.confirm-delete')
					}
					open={showConfirmUndoOrDelete}
					onConfirm={confirmUndoOrDelete}
					onCancel={() => setConfirmUndoOrDelete(false)}
					okType="link"
					placement="topLeft"
				/>
			</td>
		</tr>
	)
})
LstItem.propTypes = {
	id: PropTypes.string.isRequired
}

/**
 * @component DocumentationLst
 * @description Displays a list of documentation entries with filtering, access control, deletion, and tour support.
 * Integrates dynamic permissions, conditionally shown filters/actions, and inline interactions.
 * Includes UI tours to help users explore available functionalities.
 */
const DocumentationLst = observer(() => {
	const navigate = useNavigate()
	const { uiLstDocumentation, appType, users, tourStore } = useStores()
	const { loggedInUser, loggedInUserId } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin
	const { t } = useSmartTranslation()
	const bySearch = uiLstDocumentation.search
	const byDel = uiLstDocumentation.del
	const byAppType = uiLstDocumentation.filter.appType

	// generate tour item that will be hidden based on the permissions
	const hideTourItem = []
	// List of filters that should be hidden
	const hideFilters = [
		'priority',
		'category',
		'by-user',
		'for-user',
		'orderBy',
		'progress'
	]

	if (!(isAdmin || isSuperAdmin || users.canUser('write', 'documentation'))) {
		hideFilters.push('addNew')
		hideTourItem.push('new')
	}

	// Load documentation whenever filters change
	/**
	 * @function useEffect
	 * @description Effect hook to load documentation list every time a relevant filter value changes.
	 * Syncs the UI list store with backend data.
	 * Triggers `actLoadDocumentationList()` on filter dependency updates.
	 */
	useEffect(() => {
		actLoadDocumentationList()
	}, [bySearch, byDel, byAppType, appType.appIs])

	const documentationLst = getTourArrayByKeys('documentationLst', ['all'])
	const tourFilter = [
		...getTourArrayByKeys(
			'filter',
			['section', 'search', 'new', 'toggleBtn', 'delete'],
			hideTourItem
		)
	]

	// If user is not admin or super admin, hide appType filter
	if (!isAdmin || !isSuperAdmin) {
		hideFilters.push('appType')
	} else {
		tourFilter.push({
			title: t('single.app-type.label'),
			description: t('single.app-type.description'),
			target: () => document.getElementById('target-filter-app-type')
		})
	}

	/**
	 * @function startTour
	 * @description Starts a guided tour for the Documentation List view.
	 * If no items exist, it injects a demo item for the tour to operate on.
	 * Dynamically adjusts tour steps based on current user permissions.
	 */
	const startTour = () => {
		runInAction(() => {
			// Open advance filter when we start tour
			uiLstDocumentation.filterExpanded = true
		})

		const hasItems = uiLstDocumentation.ids.length > 0
		if (!hasItems) {
			const newId = uuidv4()
			tourStore.setDeleteItemAct(() =>
				actUndoOrDeleteDocumentation(newId, true, true).catch(
					console.error
				)
			)

			const demoObj = {
				name: 'demoItem',
				description: t('demo.item.description'),
				body: 'demo item body',
				created_by: loggedInUserId,
				appType: 'client'
			}
			actSaveDocumentation(newId, demoObj, true).catch(console.error)
		}
		tourStore.startTour('documentationLst-tour', [
			...tourFilter,
			...documentationLst
		])
	}

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('_documentation') }}
				buttonProps={{
					onClick: startTour
				}}
			/>
			<Filter
				uiStore={uiLstDocumentation}
				isForDocumentation={true}
				hide={hideFilters}
				addNew={() => {
					const newId = uuidv4()
					newDocumentation(newId).then(() => {
						navigate(`/documentation/${newId}/edit`)
					})
				}}
			/>
			<LstTable
				LstItem={LstItem}
				store={uiLstDocumentation}
				className="table-pro"
			>
				<tr id="items-lst">
					<th id="target-documentation">{t('single.name')}</th>
					<th id="target-desc">{t('single.description')}</th>
					<th id="target-info">{t('single.info')}</th>
					<th id="target-actions">{t('single.actions')}</th>
				</tr>
			</LstTable>
		</GeneralContentWrapper>
	)
})
export default DocumentationLst
